import { create } from "zustand";
import type {
  ClientConfiguration,
  ErrorConfiguration,
  LegacySettingsResponse,
} from "../../../core/backend/clients/backendClient";
import { LOG_LEVEL, logService } from "../../../core/services/logService";
import { settingsService } from "./settingsService";

interface SettingsState {
  readonly loading: boolean;
  readonly clientConfiguration: ClientConfiguration;
  readonly legacySettings: LegacySettingsResponse;
  readonly errorList: ErrorConfiguration;

  clear: () => void;

  fetchSettings: () => Promise<void>;
}

export const useSettingsStore = create<SettingsState>()((set) => ({
  loading: false,
  clientConfiguration: {},
  legacySettings: {},
  errorList: {},

  clear: () => {
    set({
      loading: false,
      clientConfiguration: {},
      legacySettings: {},
      errorList: {},
    });
  },

  fetchSettings: async () => {
    set({ loading: true });

    await settingsService
      .getClientConfiguration()
      .then((clientConfiguration: ClientConfiguration) => {
        logService.globalLog = clientConfiguration.globalLog ?? false;
        logService.logLevel =
          (clientConfiguration?.logLevel as LOG_LEVEL) ?? LOG_LEVEL.info;

        logService.log("getClientConfiguration", clientConfiguration);

        set({ clientConfiguration });
      })
      .catch((error) => {
        console.error("getClientConfiguration", error);
        set({ loading: false });
        throw error;
      });

    await settingsService
      .getLegacySettings()
      .then((legacySettings: LegacySettingsResponse) => {
        logService.log("getLegacySettings", legacySettings);

        set({ legacySettings });
      })
      .catch((error) => {
        console.error("getLegacySettings", error);
        set({ loading: false });
        throw error;
      });

    await settingsService
      .getErrorList()
      .then((errorList: ErrorConfiguration) => {
        logService.log("getErrorList", errorList);

        set({ errorList });
      })
      .catch((error) => {
        console.error("getErrorList", error);
        set({ loading: false });
        throw error;
      });

    set({ loading: false });
  },
}));
